/**
 * Base de Conhecimento como explorer de pastas (spec 2026-09-26-kb-pastas-com-acesso-por-perfil).
 *
 * Uma tela só (D9): quem lê navega; quem tem `manage` vê os controles de gestão;
 * "Detalhes" aparece para `manage`, `audit` ou admin (D10). O gate real é do
 * backend — a tela só não oferece o que ele recusaria. A aba "Repositórios" (fontes
 * do KB-Git, spec 2026-09-26 KB-Git §11) aparece para `manage` e `audit`.
 */
import { useMemo, useState } from 'react'
import { usePerguntarAoAtendente } from '@/hooks/usePerguntarAoAtendente'
import { Alert, Box, Button, Group, Paper, Skeleton, Stack, Tabs, Text, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle, IconBook2, IconBrandGit, IconBuildingPlus, IconFolder, IconFolderPlus, IconMapPin, IconPencil, IconRefresh } from '@tabler/icons-react'
import { listKbDocuments, listKbFolders, listKbRaizes, listKbRotulos, type KbBuscaDocumento } from '@/api/kb'
import { organizationsApi } from '@/api/organizations'
import { VisualizadorDeDocumento } from '@/components/kb/VisualizadorDeDocumento'
import { EmptyState } from '@/components/ui/EmptyState'
import { mensagemDeErro, useAcessoDaKb } from './acesso'
import { useAlturaAteOFim } from '@/utils/alturaDaTela'
import { algumEmIndexacao, cadastroResolvido, caminhoDe, documentosPorPasta, empresas as montarEmpresas, nomesNaKb, RAIZ_SEM_CADASTRO, raizes as montarRaizes } from './arvore'
import { ArrastarESoltar } from './ArrastarESoltar'
import { pastasQueCasam } from './busca'
import { BuscaNaKb, useBuscaNaKb } from './BuscaNaKb'
import { ConteudoDaPasta } from './ConteudoDaPasta'
import { DialogoDeLugar, type AlvoDoLugar } from './DialogoDeLugar'
import { DialogoDePasta, type AlvoDoDialogo } from './DialogoDePasta'
import { DialogoDeRaiz, type AlvoDaRaiz } from './DialogoDeRaiz'
import { DialogoDeRotulo, type AlvoDoRotulo } from './DialogoDeRotulo'
import { EnvioDeDocumento } from './EnvioDeDocumento'
import type { Triagem } from './envio'
import { ExplorerDePastas, type Selecao } from './ExplorerDePastas'
import { PainelDeDetalhes } from './PainelDeDetalhes'
import { Repositorios } from './Repositorios'
import { ResultadosDaBusca } from './ResultadosDaBusca'

export function BaseDeConhecimento() {
  const acesso = useAcessoDaKb()
  const pastas = <Pastas />
  // A aba Repositórios é só de quem gere: `/kb/git/sources` recusa quem só audita
  // (30/09/2026). Sem isto a aba apareceria e responderia 403.
  if (!acesso.gere) return pastas
  return (
    <Tabs defaultValue="pastas">
      <Tabs.List mb="md">
        <Tabs.Tab value="pastas" leftSection={<IconFolder size={14} stroke={1.5} />}>Pastas</Tabs.Tab>
        <Tabs.Tab value="repositorios" leftSection={<IconBrandGit size={14} stroke={1.5} />}>Repositórios</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="pastas">{pastas}</Tabs.Panel>
      <Tabs.Panel value="repositorios"><RepositoriosDaKb gere={acesso.gere} /></Tabs.Panel>
    </Tabs>
  )
}

/** Rótulos das organizações na KB (TASK-24787); falha cai no nome da organização. */
function useRotulosDaKb() {
  const { data = [] } = useQuery({ queryKey: ['kb-rotulos'], queryFn: () => listKbRotulos(), staleTime: 5 * 60_000 })
  return data
}

function RepositoriosDaKb({ gere }: { gere: boolean }) {
  const { data: orgs = [] } = useQuery({
    queryKey: ['organizations-lite-kb'],
    queryFn: () => organizationsApi.list(),
    staleTime: 5 * 60_000,
  })
  const rotulos = useRotulosDaKb()
  const nomesDeOrg = useMemo(() => nomesNaKb(orgs, rotulos), [orgs, rotulos])
  return <Paper withBorder p="md" radius="md"><Repositorios gere={gere} nomesDeOrg={nomesDeOrg} /></Paper>
}

function Pastas() {
  const acesso = useAcessoDaKb()
  // Desktop: árvore e conteúdo rolam cada um por conta própria. No celular, a página inteira.
  const celular = useMediaQuery('(max-width: 768px)')
  const colunas = useAlturaAteOFim<HTMLDivElement>(celular === false)
  const [busca, setBusca] = useState('')
  const [vendo, setVendo] = useState<{ id: number; pagina: number } | null>(null)
  const buscaNoServidor = useBuscaNaKb(busca)
  const perguntarAoAtendente = usePerguntarAoAtendente()
  const [selecao, setSelecao] = useState<Selecao | null>(null)
  const [dialogo, setDialogo] = useState<AlvoDoDialogo | null>(null)
  const [detalhes, setDetalhes] = useState<number | null>(null)
  const [envio, setEnvio] = useState<{ id: number; caminho: string; triagem?: Triagem } | null>(null)
  const [rotuloAlvo, setRotuloAlvo] = useState<AlvoDoRotulo | null>(null)
  const [raizAlvo, setRaizAlvo] = useState<AlvoDaRaiz | null>(null)
  const [lugarAlvo, setLugarAlvo] = useState<AlvoDoLugar | null>(null)

  const { data: nos = [], isLoading, error: erroNaArvore, isFetching, refetch: recarregarArvore } = useQuery({ queryKey: ['kb-folders'], queryFn: () => listKbFolders() })
  // Só os documentos da pasta aberta, e o recarregamento só enquanto algum deles indexa
  // (spec das raízes §13, P1 e P2): antes eram 470 KB da KB inteira a cada 15 s.
  const pastaAberta = selecao?.tipo === 'pasta' ? selecao.id : null
  const { data: listagem } = useQuery({
    queryKey: ['kb-documents', pastaAberta],
    queryFn: () => listKbDocuments({ folder_id: pastaAberta }),
    enabled: pastaAberta !== null,
    refetchInterval: (q) => (algumEmIndexacao(q.state.data?.items ?? []) ? 15_000 : false),
  })
  const { data: orgs = [], isLoading: carregandoOrgs } = useQuery({
    queryKey: ['organizations-lite-kb'],
    queryFn: () => organizationsApi.list(),
    staleTime: 5 * 60_000,
  })

  // Empresas acima das organizações (spec 2026-10-21-kb-raizes). Falhar não esconde
  // nada: sem cadastro, tudo fica em Amazonas Inovare › Projetos, como o padrão (R7).
  const { data: cadastroDeRaizes } = useQuery({ queryKey: ['kb-raizes'], queryFn: () => listKbRaizes(), staleTime: 5 * 60_000 })
  const rotulos = useRotulosDaKb()
  const nomesOficiais = useMemo(() => new Map(orgs.map((o) => [o.id, o.name])), [orgs])
  const nomesDeOrg = useMemo(() => nomesNaKb(orgs, rotulos), [orgs, rotulos])
  // Quem gere precisa da raiz de toda organização que enxerga, mesmo sem pasta.
  const raizes = useMemo(
    () => montarRaizes(nos, nomesDeOrg, acesso.gere ? orgs.map((o) => o.id) : []),
    [nos, nomesDeOrg, orgs, acesso.gere],
  )
  const cadastro = useMemo(() => cadastroResolvido(cadastroDeRaizes), [cadastroDeRaizes])
  const empresas = useMemo(
    () => montarEmpresas(raizes, cadastroDeRaizes, { mostrarVazias: acesso.admin }),
    [raizes, cadastroDeRaizes, acesso.admin],
  )
  const docsPorPasta = useMemo(() => documentosPorPasta(listagem?.items ?? []), [listagem])
  const porId = useMemo(() => new Map(nos.map((n) => [n.id, n])), [nos])

  const abertas = useMemo(() => {
    const s = new Set<number>()
    let atual = selecao?.tipo === 'pasta' ? porId.get(selecao.id) : undefined
    while (atual) {
      s.add(atual.id)
      atual = atual.parent_id === null ? undefined : porId.get(atual.parent_id)
    }
    return s
  }, [selecao, porId])

  const abrir = (id: number) => setSelecao({ tipo: 'pasta', id })
  const buscando = busca.trim().length > 0
  // Sair da busca devolve a pasta que estava aberta: a seleção não muda enquanto se busca.
  const sairDaBusca = () => setBusca('')
  const selecionar = (s: Selecao) => { setBusca(''); setSelecao(s) }
  const pastasDaBusca = useMemo(() => pastasQueCasam(nos, busca), [nos, busca])
  const abrirDocumento = (d: KbBuscaDocumento, pagina: number | null) => {
    if (d.pasta_id !== null) selecionar({ tipo: 'pasta', id: d.pasta_id })
    setVendo({ id: d.document_id, pagina: pagina ?? 1 })
  }
  const selecionada = selecao?.tipo === 'pasta' ? porId.get(selecao.id) : undefined
  // A empresa da plataforma aparece mesmo sem pasta da plataforma que a pessoa leia
  // (as organizações dela estão dentro): o painel existe vazio, em vez de sumir.
  const raizAberta = selecao?.tipo === 'raiz'
    ? raizes.find((r) => r.organizationId === selecao.organizationId)
      ?? (selecao.organizationId === null ? { organizationId: null, rotulo: cadastro.plataforma.nome, pastas: [] } : undefined)
    : undefined
  const podeCriarNaRaiz = (org: number | null) => acesso.gere && (org !== null || acesso.admin)
  const empresaAberta = selecao?.tipo === 'empresa' ? empresas.find((e) => e.raizId === selecao.raizId) : undefined
  // Nova empresa entra depois das que existem; renomear só a que tem cadastro.
  const novaEmpresa = () => setRaizAlvo({ modo: 'criar', ordem: Math.max(0, ...cadastro.todas.map((r) => r.ordem)) + 1 })
  const botoesDaEmpresa = (raizId: number, nome: string) => acesso.admin && raizId !== RAIZ_SEM_CADASTRO && (
    <>
      <Button size="xs" variant="default" leftSection={<IconPencil size={14} />}
        onClick={() => setRaizAlvo({ modo: 'renomear', raizId, nomeAtual: nome })}>
        Renomear
      </Button>
      <Button size="xs" variant="default" leftSection={<IconBuildingPlus size={14} />} onClick={novaEmpresa}>
        Nova empresa
      </Button>
    </>
  )
  const rotuloDaRaiz = (org: number | null) => raizes.find((r) => r.organizationId === org)?.rotulo ?? 'Topo'


  if (isLoading || (acesso.gere && carregandoOrgs)) {
    return <Skeleton height={320} radius="md" />
  }

  const tentarDeNovo = (
    <Button size="xs" variant="default" leftSection={<IconRefresh size={14} />} loading={isFetching} onClick={() => recarregarArvore()}>
      Tentar de novo
    </Button>
  )

  // Erro não é "sem pasta": 503 com a KB desligada ou 500 diriam, no estado
  // vazio, que falta perfil a quem não falta nada. Com a árvore já carregada, o erro
  // de uma recarga não a apaga (consoleai#25334): a queda de conexão é passageira.
  if (erroNaArvore && nos.length === 0) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Não foi possível carregar as pastas">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{mensagemDeErro(erroNaArvore, 'Tente de novo em instantes.')}</Text>
          {tentarDeNovo}
        </Stack>
      </Alert>
    )
  }

  if (raizes.length === 0) {
    return (
      <Stack gap="md">
        <EmptyState
          icon={IconBook2}
          title="Nenhuma pasta disponível para você"
          description="As pastas aparecem aqui quando o seu perfil de acesso recebe leitura em alguma delas. Fale com quem administra a Base de Conhecimento da sua organização."
        />
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      {erroNaArvore && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} p="xs">
          <Group justify="space-between" gap="xs">
            <Text size="sm">Não foi possível atualizar as pastas. A árvore abaixo pode estar desatualizada.</Text>
            {tentarDeNovo}
          </Group>
        </Alert>
      )}
      <ArrastarESoltar nos={nos} acesso={acesso} rotuloDaRaiz={rotuloDaRaiz}
        onArquivos={(p, triagem) => setEnvio({ id: p.id, caminho: caminhoDe(nos, p.id), triagem })}>
        <Group ref={colunas.ref} align="flex-start" gap="md" wrap="nowrap" data-testid="kb-colunas"
          style={colunas.altura ? { height: colunas.altura } : undefined}>
          <Paper withBorder p="xs" radius="md" w={300} miw={240}
            style={{ flexShrink: 0, ...(colunas.altura ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}) }}>
            <BuscaNaKb valor={busca} onMudar={setBusca} onConfirmar={buscaNoServidor.confirmar} onLimpar={sairDaBusca} />
            <Box data-testid="kb-arvore-rolagem" style={colunas.altura ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>
              <ExplorerDePastas empresas={empresas} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={selecionar} />
            </Box>
          </Paper>
          <Box data-testid="kb-conteudo-rolagem"
            style={{ flex: 1, minWidth: 0, ...(colunas.altura ? { height: '100%', overflowY: 'auto' } : {}) }}>
            <Paper withBorder p="md" radius="md">
              {buscando ? (
                <ResultadosDaBusca
                  q={busca}
                  pastas={pastasDaBusca}
                  dados={buscaNoServidor.atualizado ? buscaNoServidor.consulta.data : undefined}
                  carregando={!buscaNoServidor.atualizado || buscaNoServidor.consulta.isFetching}
                  erro={buscaNoServidor.consulta.error}
                  pastaExiste={(id) => porId.has(id)}
                  onAbrirPasta={(id) => selecionar({ tipo: 'pasta', id })}
                  onAbrirDocumento={abrirDocumento}
                  onPerguntarAoAtendente={perguntarAoAtendente}
                />
              ) : selecionada ? (
                <ConteudoDaPasta
                  key={selecionada.id}
                  pastaId={selecionada.id}
                  acesso={acesso}
                  documentos={docsPorPasta.get(selecionada.id) ?? []}
                  pastasDaOrg={nos.filter((n) => n.organization_id === selecionada.organization_id)}
                  onAbrir={abrir}
                  onNovaSubpasta={(pai) => setDialogo({ modo: 'criar', organizationId: pai.organizationId, parentId: pai.id, ondeRotulo: pai.caminho })}
                  onEditar={(id) => setDialogo({ modo: 'editar', pastaId: id })}
                  onDetalhes={setDetalhes}
                  onEnviar={setEnvio}
                  onExcluida={(pai) => setSelecao(pai === null ? { tipo: 'raiz', organizationId: selecionada.organization_id } : { tipo: 'pasta', id: pai })}
                />
              ) : selecao?.tipo === 'pasta' && isFetching ? (
                // Pasta recém-criada: selecionada antes de a árvore voltar com ela.
                <Skeleton height={120} />
              ) : raizAberta ? (
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Stack gap={0}>
                      <Title order={3}>{raizAberta.organizationId === null ? cadastro.plataforma.nome : raizAberta.rotulo}</Title>
                      {raizAberta.organizationId !== null && nomesOficiais.has(raizAberta.organizationId)
                        && nomesOficiais.get(raizAberta.organizationId) !== raizAberta.rotulo && (
                        <Text size="xs" c="dimmed">Organização {nomesOficiais.get(raizAberta.organizationId)}</Text>
                      )}
                    </Stack>
                    <Group gap="xs">
                      {raizAberta.organizationId === null && botoesDaEmpresa(cadastro.plataforma.id, cadastro.plataforma.nome)}
                      {raizAberta.organizationId !== null && acesso.admin && (
                        <Button size="xs" variant="default" leftSection={<IconMapPin size={14} />}
                          onClick={() => setLugarAlvo({
                            organizationId: raizAberta.organizationId!,
                            rotulo: raizAberta.rotulo,
                            atual: cadastro.lugarDe(raizAberta.organizationId!),
                          })}>
                          Mudar de lugar
                        </Button>
                      )}
                      {raizAberta.organizationId !== null && acesso.gere && (
                        <Button size="xs" variant="default" leftSection={<IconPencil size={14} />}
                          onClick={() => setRotuloAlvo({
                            organizationId: raizAberta.organizationId!,
                            nomeDaOrganizacao: nomesOficiais.get(raizAberta.organizationId!) ?? raizAberta.rotulo,
                            rotuloAtual: raizAberta.rotulo,
                          })}>
                          Renomear
                        </Button>
                      )}
                      {podeCriarNaRaiz(raizAberta.organizationId) && (
                        <Button size="xs" leftSection={<IconFolderPlus size={14} />}
                          onClick={() => setDialogo({ modo: 'criar', organizationId: raizAberta.organizationId, parentId: null, ondeRotulo: raizAberta.rotulo })}>
                          Nova pasta
                        </Button>
                      )}
                    </Group>
                  </Group>
                  {raizAberta.organizationId === null && (
                    <Text size="sm" c="dimmed">Pastas da plataforma: valem para quem tem o perfil, em qualquer organização.</Text>
                  )}
                  {raizAberta.pastas.length === 0 ? (
                    <Text size="sm" c="dimmed">Nenhuma pasta ainda.</Text>
                  ) : (
                    <Text size="sm" c="dimmed">{raizAberta.pastas.length} {raizAberta.pastas.length === 1 ? 'pasta' : 'pastas'} no topo. Escolha uma à esquerda.</Text>
                  )}
                </Stack>
              ) : empresaAberta ? (
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Title order={3}>{empresaAberta.nome}</Title>
                    <Group gap="xs">{botoesDaEmpresa(empresaAberta.raizId, empresaAberta.nome)}</Group>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {(() => {
                      const n = empresaAberta.topo.length + (empresaAberta.projetos?.organizacoes.length ?? 0)
                      return n === 0
                        ? 'Nenhuma organização nesta empresa ainda. Para trazer uma, abra a organização e use Mudar de lugar.'
                        : `${n} ${n === 1 ? 'organização' : 'organizações'}. A empresa agrupa organizações e não guarda documento.`
                    })()}
                  </Text>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">Escolha uma pasta à esquerda.</Text>
              )}
            </Paper>
          </Box>
        </Group>
      </ArrastarESoltar>

      <DialogoDePasta alvo={dialogo} pastas={nos} onClose={() => setDialogo(null)} onCriada={abrir} />
      <DialogoDeRotulo alvo={rotuloAlvo} onClose={() => setRotuloAlvo(null)} />
      <DialogoDeRaiz alvo={raizAlvo} onClose={() => setRaizAlvo(null)} onCriada={(raizId) => selecionar({ tipo: 'empresa', raizId })} />
      <DialogoDeLugar alvo={lugarAlvo} raizes={cadastro.todas.filter((r) => r.id !== RAIZ_SEM_CADASTRO)} onClose={() => setLugarAlvo(null)} />
      <PainelDeDetalhes pastaId={detalhes} onClose={() => setDetalhes(null)} />
      <EnvioDeDocumento pasta={envio} onClose={() => setEnvio(null)} onAbrirPasta={(id) => selecionar({ tipo: 'pasta', id })} />
      <VisualizadorDeDocumento documentId={vendo?.id ?? null} paginaInicial={vendo?.pagina} onClose={() => setVendo(null)} />
    </Stack>
  )
}

export default BaseDeConhecimento
